/**
 * Cliente HTTP do frontend.
 *
 * REGRA: o frontend só comunica com `/api/*` (BFF local).
 * Nenhuma URL externa, chave ou token de terceiros deve existir aqui.
 */

import { fetchWithRetry } from '@/basedata/fetchWithRetry';
import {
  translateApiError,
  translateApiErrorStatus,
} from '@/i18n/translateApiError';
import type { EventContextResponse, TenantBrandingResponse } from '@/types/branding';
import type {
  CancelCompanionResponse,
  CompanionPhotoResponse,
  CompanionInviteLinkResponse,
  CompanionsListResponse,
  IdentifyResponse,
  RegisterCompanionResponse,
  LinkResponse,
  UpdateCompanionPayload,
} from '@/types/guestOnboarding';
import type {
  ConsentAcceptPayload,
  ConsentAcceptResponse,
  ConsentTermsResponse,
} from '@/types/consent';
import type { UserProfileResponse } from '@/types/profile';
import type {
  SignupApiSubmitPayload,
  SignupSubmitResponse,
} from '@/types/signup';

export type SelfieSubmitResponse = {
  ok: true
  confidence: number
  status: string
}

const API_BASE = '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // `fetchWithRetry` repete SÓ leitura (GET/HEAD) quando o BFF ainda não subiu —
  // escrita passa direto, uma tentativa só. Ver `fetchWithRetry.ts`.
  const response = await fetchWithRetry(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let message = translateApiErrorStatus(response.status);
    try {
      const body = (await response.json()) as { error?: string; code?: string };
      if (body.code) {
        message = translateApiError(body.code);
      } else if (body.error) {
        message = translateApiError(body.error);
      }
    } catch {
      // resposta não-JSON
    }
    throw new ApiError(message, response.status);
  }

  return response.json() as Promise<T>;
}

export interface HealthResponse {
  status: string;
  timestamp: string;
}

export interface ExampleItem {
  id: string;
  title: string;
}

export interface ExampleItemsResponse {
  items: ExampleItem[];
  source: 'mock' | 'external';
}

export const api = {
  health: () => request<HealthResponse>('/health'),

  example: {
    getItems: () => request<ExampleItemsResponse>('/example/items'),
  },

  branding: {
    get: (slug: string) =>
      request<TenantBrandingResponse>(
        `/branding?slug=${encodeURIComponent(slug)}`,
      ),
  },

  eventContext: {
    get: (slug: string) =>
      request<EventContextResponse>(
        `/event-context?slug=${encodeURIComponent(slug)}`,
      ),
  },

  onboarding: {
    identify: (payload: { document: string; sessionId?: string }) =>
      request<IdentifyResponse>('/onboarding/identify', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    link: (payload: { token: string; confirm?: boolean; sessionId?: string }) =>
      request<LinkResponse>('/onboarding/link', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    companionPhoto: (payload: {
      fullName: string;
      base64Image: string;
      consentConfirmed: true;
      sessionId?: string;
    }) =>
      request<CompanionPhotoResponse>('/onboarding/companions/photo', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    registerCompanion: (payload: {
      sessionId: string
      fullName: string
      birthDate?: string
      document?: string
      base64Image?: string
      consentConfirmed?: boolean
    }) =>
      request<RegisterCompanionResponse>('/onboarding/companions', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),

    listCompanions: (params: { sessionId: string }) =>
      request<CompanionsListResponse>(
        `/onboarding/companions?sessionId=${encodeURIComponent(params.sessionId)}`,
      ),

    companionPhotoFor: (
      companionId: string,
      payload: {
        sessionId: string
        base64Image: string
        consentConfirmed: true
        fullName?: string
      },
    ) =>
      request<RegisterCompanionResponse>(
        `/onboarding/companions/${encodeURIComponent(companionId)}/photo`,
        { method: 'POST', body: JSON.stringify(payload) },
      ),

    updateCompanion: (
      companionId: string,
      payload: UpdateCompanionPayload & { sessionId: string },
    ) =>
      request<RegisterCompanionResponse>(
        `/onboarding/companions/${encodeURIComponent(companionId)}`,
        { method: 'PATCH', body: JSON.stringify(payload) },
      ),

    cancelCompanion: (companionId: string, params: { sessionId: string }) =>
      request<CancelCompanionResponse>(
        `/onboarding/companions/${encodeURIComponent(companionId)}?sessionId=${encodeURIComponent(params.sessionId)}`,
        { method: 'DELETE' },
      ),

    companionInviteLink: (companionId: string, payload: { sessionId: string }) =>
      request<CompanionInviteLinkResponse>(
        `/onboarding/companions/${encodeURIComponent(companionId)}/invite-link`,
        { method: 'POST', body: JSON.stringify(payload) },
      ),
  },

  consent: {
    getTerms: () => request<ConsentTermsResponse>('/consent/terms'),
    accept: (payload: ConsentAcceptPayload) =>
      request<ConsentAcceptResponse>('/consent/accept', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  signup: {
    submit: (payload: SignupApiSubmitPayload) =>
      request<SignupSubmitResponse>('/signup', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  selfie: {
    // O sessionId do onboarding vai no corpo só para o LOG: se a criação da
    // sessão de vivacidade falhar, é o que amarra o erro ao convidado no painel.
    createSession: (payload?: { sessionId?: string }) =>
      request<{ sessionId: string }>('/selfie/session', {
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
      }),
    /**
     * Reporta uma falha de captura que só o browser conhece (câmera negada,
     * sem câmera, iluminação, rosto desalinhado, timeout do desafio) para ela
     * aparecer na modal de logs do painel de convidados.
     *
     * ⚠️ Nunca mande imagem aqui — o DTO do service recusa e o log é justamente
     * o lugar onde biometria não pode entrar.
     */
    captureLog: (payload: {
      sessionId: string
      code: string
      message?: string
      details?: Record<string, unknown>
    }) =>
      request<{ ok: true }>('/selfie/capture-log', {
        method: 'POST',
        body: JSON.stringify(payload),
        // O log sai às vezes logo antes de a página recarregar (câmera que não
        // abriu) ou de o convidado sair — keepalive deixa ele chegar mesmo assim.
        keepalive: true,
      }),
    // sessionId = sessão do onboarding (aceite); livenessSessionId = sessão AWS
    // Face Liveness (só no modo liveness — o BFF valida na AWS por ele).
    submit: (payload: {
      base64Image: string
      sessionId: string
      livenessSessionId?: string
      eventSlug?: string
    }) =>
      request<SelfieSubmitResponse>('/selfie/submit', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  user: {
    getProfile: (params?: { sessionId?: string }) => {
      const sessionId = params?.sessionId?.trim();
      const query =
        sessionId !== undefined && sessionId !== ''
          ? `?sessionId=${encodeURIComponent(sessionId)}`
          : '';
      return request<UserProfileResponse>(`/user/profile${query}`);
    },
  },
};
